import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Card, Flex, Heading } from "@radix-ui/themes";
import { PAGE_NUMBER, PAGE_SIZE } from "../../../utils/constants";
import { useGetAcademicYearQuery } from "../../../hooks/useGetAcademicYearQuery";
import { useGetUniversitiesQuery } from "../../../hooks/useGetUniversitiesQuery";
import { useGetFacultiesQuery } from "../../../hooks/useGetFacultiesQuery";
import { useGetDepartamentsQuery } from "../../../hooks/useGetDepartamentsQuery";
import { useGetCoursesQuery } from "../../../hooks/useGetCoursesQuery";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import { Lists } from "../../../components/common/Lists";
import TableComponent from "../../../components/common/Tables";
import { getPaymentReport } from "../../../services/finance/reports";
import { useGetPaymentTypesQuery } from "../../../hooks/useGetPaymentTypesQuery";
import { FiltersGetPaymentReportType } from "../../../types/Finance";
import { RegimeOptions } from "../../../utils/studentUtils";


// Define os filtros para consulta de relatório

const columnNamesMap: { [key: string]: string } = {
  faculty: "Faculdade",
  departament: "Departamento",
  course: "Curso",
  courseLevel: "Nível do Curso",
  studentLevel: "Ano do Estudante",
  regime: "Regime",
  paymentType: "Tipo de emolumento",
  totalPayed: "Total Pago",
  month: "Mês",
  year: "Ano",
  semester: "Semestre",
};
const columnNamesMapTotal: { [key: string]: string } = {
  totalPayed: "Total Pago",
};

export const GetPaymentReport = () => {
  const [currentPage, setCurrentPage] = useState(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const { control, handleSubmit, watch, setValue, getValues, reset,  formState: { errors } } =
    useForm<FiltersGetPaymentReportType>({
      defaultValues: {},
      mode: "onBlur",
    });

  const filters = watch();

  const { data: academicYears, isLoading: isLoadingAcademicYears } =
    useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });

  const { data: universities, isLoading: isLoadingUniversities } =
    useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

  const { data: faculties, isLoading: isLoadingFaculties } = useGetFacultiesQuery(
    { PageNumber: 1, PageSize: 400, IdUniversity: getValues("IdUniversity") }
  );

  const { data: departaments, isLoading: isLoadingDepartaments } =
    useGetDepartamentsQuery(
      { PageNumber: 1, PageSize: 400, IdFaculty: getValues("IdFaculty") },
      { enabled: !!getValues("IdFaculty") }
    );

  const { data: courses, isLoading: isLoadingCourses } = useGetCoursesQuery(
    { PageNumber: 1, PageSize: 400, IdDepartament: getValues("IdDepartament") },
    { enabled: !!getValues("IdDepartament") }
  );
      const { data: paymentTypesData, status: paymentTypesStatus, isError: isErrorPaymentTypes, isLoading: isLoadingPaymentTypes } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });
  
      const memoizedFilters = useMemo(() => JSON.stringify(filters), [filters]);

  const { data, status, refetch } = useQuery(["getPaymentReport",memoizedFilters],async ()=>{
       const response = await getPaymentReport(filters);
       return response;
  }, {
    keepPreviousData: true
  });

  useEffect(() => {
    if (academicYears?.items?.length) {
      const mostRecentYear = academicYears.items[academicYears.items.length - 1];
      if (!getValues("Year")) { 
        setValue("Year", +mostRecentYear.year);
      }
    }
  }, [academicYears, setValue, getValues]);

  const onSubmit = handleSubmit(() => {
    refetch();
  });

  return (
    <>
      <Card>
        <Flex gap="4" direction="column">
          <Heading>Pagamentos</Heading>
          <form onSubmit={onSubmit}>
            <Flex gap="4" direction="column">
              <Flex gap="2">
                <SelectComponent
                  name="Year"
                  label="Ano Acadêmico"
                  control={control}
                  errors={errors}
                  options={academicYears?.items.map((ac) => ({
                    value: ac.year ?? "",
                    label: ac.year ?? "",
                  }))}
                  itemsLoading={isLoadingAcademicYears}
                  style={{width:'100%'}}
                />
                <TextFieldComponent
                  name="Month"
                  label="Mês"
                  style={{width:'100%'}}
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Mês", type: "number" }}
                />
                <TextFieldComponent
                  name="Semester"
                  label="Semestre"
                  style={{width:'100%'}}
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Semestre", type: "number" }}
                />
              </Flex>
              <Flex gap="2">
              <RadioGroupComponent name="Regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "1", label: "Laboral" }
                , { value: "2", label: "Pós Laboral" }
                , { value: "3", label: "Distância" }
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: '16px' }
              }}
            />
                <TextFieldComponent
                  style={{ width: '100%' }}
                  name="StudentLevel"
                  label="Nível do Estudante"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Ano", type: "number" }}
                />
              </Flex>
              <Flex gap="4">
                <SelectComponent
                style={{ width: '100%' }}
                  name="IdUniversity"
                  label="Instituição"
                  control={control}
                  errors={errors}
                  options={universities?.items.map((u) => ({
                    value: u.id ?? "",
                    label: u.description ?? "",
                  }))}
                  itemsLoading={isLoadingUniversities}
                  onSelect={(id) => {
                    setValue("IdUniversity", id);
                    setValue("IdFaculty", "");
                  }}
                />
                <SelectComponent
                style={{ width: '100%' }}
                  name="IdFaculty"
                  label="Faculdade"
                  control={control}
                  errors={errors}
                  options={faculties?.items.map((faculty) => ({
                    value: faculty.id ?? "",
                    label: faculty.description ?? "",
                  }))}
                  itemsLoading={isLoadingFaculties}
                  onSelect={(id) => setValue("IdFaculty", id)}
                />
              </Flex>
              <Flex gap="4">
                <SelectComponent
                  name="IdDepartament"
                  label="Departamento"
                  control={control}
                  style={{ width: '100%' }}
                  errors={errors}
                  options={departaments?.items.map((departament) => ({
                    value: departament.id ?? "",
                    label: departament.description ?? "",
                  }))}
                  itemsLoading={isLoadingDepartaments}
                  onSelect={(id) => setValue("IdDepartament", id)}
                />
                <SelectComponent
                  name="IdCourse"
                  label="Curso"
                  control={control}
                  errors={errors}
                  style={{ width: '100%' }}
                  options={courses?.items.map((course) => ({
                    value: course.id ?? "",
                    label: course.name ?? "",
                  }))}
                  itemsLoading={isLoadingCourses}
                  onSelect={(id) => setValue("IdCourse", id)}
                />
                <SelectComponent
                  name="IdPaymentType"
                  label="Tipo de emolumento"
                  control={control}
                  errors={errors}
                  options={paymentTypesData?.items.map((p) => ({
                    value: p.id ?? "",
                    label: p.description ?? "",
                  }))}
                  style={{ width: '100%' }}
                  itemsLoading={isLoadingPaymentTypes}
                />
              </Flex>
            </Flex>
          </form>
        </Flex>
      </Card>

      <Lists
        status={status}
        TableComponent={() => (
          <TableComponent
            columns={Object.keys(columnNamesMap)}
                items={data?.map((d: any) => {
                    return { ...d,
                         regime: RegimeOptions[d?.regime!] ?? d?.regime,
                         semester: `${d.semester}º Semestre`

                     }
                })}
            options={[]}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalItems={data?.totalItems}
            totalColumns={Object.keys(columnNamesMapTotal)}
            enableSearchByBtn={false}
            enableSearchByEnter= {false}
            />
        )}
        handleClickCreate={undefined}
      />
    </>
  );
};

